import { useEffect } from 'react';
import { useTracker } from '@archipro-website/tracker';
import { TwoPaneModal } from '@rocco/components/two-pane-modal';
import type { LoginFlow } from '~/modules/user/hook/use-login-flow';
import type { AuthSource } from '~/modules/user/type';
import type { PublicUser } from '~/routes/remix-api.user.public-user';
import { GATE_PANE_BRAND_DEFAULT } from '~/modules-rocco/user/auth-gate/gate-pane-image';
import { isReturningPasskeyDevice } from '~/modules/user/passkey/passkey-device-match';
import { AuthModalBrandPane } from './AuthModalBrandPane';
import { AuthPanel } from './AuthPanel';
import { getAuthBrandStatement, getAuthIntentCopy } from './auth-intent-copy';

export interface AuthModalProps {
    open: boolean;
    authSource: AuthSource;
    flow?: LoginFlow;
    redirect?: string;
    allowExternalUrls?: boolean;
    skipPostSignUp?: boolean;
    disableCloseButton?: boolean;
    /** Overrides the intent registry headline. */
    header?: string;
    /** Overrides the intent registry subcopy. */
    subtitle?: string;
    /** Contextual line shown above the form (e.g. enquiry gating message). */
    message?: string;
    defaultPublicUser?: PublicUser;
    /**
     * Replaces the default brand pane (imagery + AP mark + statement) with
     * custom left-pane content — e.g. the digital publications subscription
     * checklist. Rendered inside the pane container (size, rounding and
     * overflow handled here); the component owns its own background.
     */
    brandPane?: React.ReactNode;
    onCancel?: () => void;
    onAuthSuccess: () => void;
}

/**
 * Revamped two-pane auth modal (BC-4826): left brand pane, right dynamic step
 * panel. Email-first — the initial step offers social providers and an email
 * field; entering an email runs account detection and routes to the login
 * ("Welcome back") or sign-up form. Rendered by LoginSignUpModal when the
 * auth_modal_2026 feature flag is on. The right-pane step machine lives in
 * AuthPanel, which the enquiry gate reuses in its own shell.
 */
export const AuthModal = (props: AuthModalProps) => {
    const {
        open,
        authSource,
        flow,
        redirect,
        allowExternalUrls,
        skipPostSignUp,
        disableCloseButton,
        header,
        subtitle,
        message,
        defaultPublicUser,
        brandPane,
        onCancel,
        onAuthSuccess,
    } = props;

    const tracker = useTracker();
    const intentCopy = getAuthIntentCopy(authSource);

    useEffect(() => {
        if (!open) return;
        tracker.log('AuthModalImpression', {
            url: new URL(window.location.href),
            data: { Source: authSource },
            ga4Data: { source: authSource },
            targetTracker: ['archiproTracker', 'ga4Tracker'],
        });
    }, [open, authSource, tracker]);

    return (
        <TwoPaneModal
            corners="rounded"
            open={open}
            onOpenChange={(nextOpen) => {
                if (!nextOpen) onCancel?.();
            }}
            title={header || intentCopy.headline}
            closeButton={!disableCloseButton}
            bodyPadding="roomy"
            pane={
                brandPane ?? (
                    // No item stands behind a plain sign-in, so brand imagery
                    // is the intended answer here rather than a fallback.
                    <AuthModalBrandPane
                        statement={getAuthBrandStatement(
                            authSource,
                            isReturningPasskeyDevice(),
                        )}
                        image={GATE_PANE_BRAND_DEFAULT}
                    />
                )
            }
        >
            <AuthPanel
                authSource={authSource}
                flow={flow}
                redirect={redirect}
                allowExternalUrls={allowExternalUrls}
                skipPostSignUp={skipPostSignUp}
                header={header}
                subtitle={subtitle}
                message={message}
                defaultPublicUser={defaultPublicUser}
                onAuthSuccess={onAuthSuccess}
            />
        </TwoPaneModal>
    );
};
